// Manifest validation. The browser does not trust the manifest blindly: it must describe a contiguous,
// gap-free cover of the file with well-formed SHA-256 hashes, or nothing is downloaded.
import { HEX64 } from './sha256.js';
import { TransferFailure, MESSAGES } from './errors.js';

export const MAX_PIECE_LENGTH = 64 * 1024 * 1024; // a single piece is held in memory while it is verified
export const MAX_PIECES = 5_000_000;

const isInt = (v) => Number.isSafeInteger(v) && v >= 0;

function fail(why) {
  const e = new TransferFailure('bad-manifest', MESSAGES['bad-manifest']);
  e.detail = why;
  return e;
}

/**
 * @param {any} m raw manifest from the API
 * @returns normalized manifest `{ resourceId, version, name, mime, sizeBytes, pieceLength, sha256, pieces, torrent }`
 */
export function validateManifest(m) {
  if (!m || typeof m !== 'object') throw fail('not an object');
  const { resourceId, version, name, mime, sizeBytes, pieceLength, sha256, pieces } = m;
  if (typeof resourceId !== 'string' || !resourceId) throw fail('resourceId');
  if (version == null || (typeof version !== 'string' && typeof version !== 'number')) throw fail('version');
  if (typeof name !== 'string' || !name) throw fail('name');
  if (!isInt(sizeBytes)) throw fail('sizeBytes');
  if (!isInt(pieceLength) || pieceLength < 1 || pieceLength > MAX_PIECE_LENGTH) throw fail('pieceLength');
  if (typeof sha256 !== 'string' || !HEX64.test(sha256.toLowerCase())) throw fail('sha256');
  if (!Array.isArray(pieces)) throw fail('pieces');
  const expected = sizeBytes === 0 ? 0 : Math.ceil(sizeBytes / pieceLength);
  if (pieces.length !== expected || pieces.length > MAX_PIECES) throw fail('piece count');
  const out = new Array(pieces.length);
  let offset = 0;
  for (let i = 0; i < pieces.length; i++) {
    const p = pieces[i];
    if (!p || p.index !== i) throw fail(`piece ${i} index`);
    if (p.offset !== offset) throw fail(`piece ${i} offset`);
    const len = Math.min(pieceLength, sizeBytes - offset);
    if (p.length !== len) throw fail(`piece ${i} length`);
    if (typeof p.sha256 !== 'string' || !HEX64.test(p.sha256.toLowerCase())) throw fail(`piece ${i} sha256`);
    out[i] = { index: i, offset, length: len, sha256: p.sha256.toLowerCase() };
    offset += len;
  }
  return {
    resourceId,
    version: String(version),
    name,
    mime: typeof mime === 'string' && mime ? mime : 'application/octet-stream',
    sizeBytes,
    pieceLength,
    sha256: sha256.toLowerCase(),
    pieces: out,
    torrent: m.torrent && typeof m.torrent === 'object' ? m.torrent : null,
  };
}

/** File extension (lowercase, no dot) for presentation only. */
export function extOf(name) {
  const m = /\.([a-z0-9]{1,8})$/i.exec(name || '');
  return m ? m[1].toLowerCase() : '';
}
